import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listTaskForms } from '@epics/taskFormManagement/api/listTaskFormsApiService';
import { getTaskForm } from '@epics/taskFormManagement/api/getTaskFormApiService';
import { createCertificateTemplate } from '../api/createCertificateTemplateApiService';
import { CERTIFICATE_LABELS } from '../constants/labels';
import type { CreateCertificateTemplateFormState } from '../types';

// ─── Form error shape ─────────────────────────────────────────────────────────

export interface FormErrors {
  certTemplateName?: string;
  formTemplateId?: string;
  headerField?: string;
  footerField?: string;
  subtitle?: string;
  selectedFormFieldIds?: string;
  general?: string;
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateForPreview(
  state: CreateCertificateTemplateFormState
): FormErrors {
  const errors: FormErrors = {};

  if (!state.certTemplateName.trim()) {
    errors.certTemplateName = CERTIFICATE_LABELS.ERROR_TEMPLATE_NAME_REQUIRED;
  } else if (state.certTemplateName.length > 200) {
    errors.certTemplateName = CERTIFICATE_LABELS.ERROR_TEMPLATE_NAME_MAX;
  }

  if (!state.formTemplateId) {
    errors.formTemplateId = CERTIFICATE_LABELS.ERROR_FORM_REQUIRED;
  }

  if (!state.headerField.trim()) {
    errors.headerField = CERTIFICATE_LABELS.ERROR_HEADER_REQUIRED;
  } else if (state.headerField.length > 200) {
    errors.headerField = CERTIFICATE_LABELS.ERROR_HEADER_MAX;
  }

  if (!state.footerField.trim()) {
    errors.footerField = CERTIFICATE_LABELS.ERROR_FOOTER_REQUIRED;
  } else if (state.footerField.length > 200) {
    errors.footerField = CERTIFICATE_LABELS.ERROR_FOOTER_MAX;
  }

  if (state.subtitle && state.subtitle.length > 500) {
    errors.subtitle = CERTIFICATE_LABELS.ERROR_SUBTITLE_MAX;
  }

  if (state.selectedFormFieldIds.length === 0) {
    errors.selectedFormFieldIds = CERTIFICATE_LABELS.ERROR_FIELDS_REQUIRED;
  }

  return errors;
}

function hasErrors(errors: FormErrors): boolean {
  return Object.values(errors).some(Boolean);
}

// ─── Initial state ────────────────────────────────────────────────────────────

const INITIAL_FORM_STATE: CreateCertificateTemplateFormState = {
  certTemplateName: '',
  formTemplateId: '',
  formTemplateName: '',
  formTemplateDisplayId: '',
  headerField: '',
  footerField: '',
  subtitle: '',
  includeTechnicianDetail: false,
  selectedFormFieldIds: [],
  availableFormFields: [],
};

const FORM_PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 500;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages the full create-certificate-template flow.
 *
 * Responsibilities:
 * - Loads available task forms with server-side search (500 ms debounce) and
 *   infinite scroll (pagination via `handleFormScrollEnd`).
 * - Loads available fields whenever the selected task form changes.
 * - Validates all fields before opening the preview step.
 * - Submits the payload and navigates to the list page on success.
 * - Manages cancel-confirmation modal and error snackbar state.
 */
export function useCreateCertTemplate() {
  const navigate = useNavigate();

  const [formState, setFormState] =
    useState<CreateCertificateTemplateFormState>(INITIAL_FORM_STATE);

  // ─── Form-selector state (paginated + searchable) ────────────────────────────
  const [formOptions, setFormOptions] = useState<
    Array<{ value: string; label: string; formId: string }>
  >([]);
  const [formSearch, setFormSearch] = useState('');
  const [formPage, setFormPage] = useState(1);
  const [formOptionsHasNextPage, setFormOptionsHasNextPage] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingMoreForms, setLoadingMoreForms] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [loadingFields, setLoadingFields] = useState(false);
  const [isPreviewValidating, setIsPreviewValidating] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [validationAttemptCount, setValidationAttemptCount] = useState(0);

  // ─── Dirty check ────────────────────────────────────────────────────────────

  const isDirty =
    formState.certTemplateName.trim() !== '' ||
    formState.formTemplateId !== '' ||
    formState.headerField.trim() !== '' ||
    formState.footerField.trim() !== '' ||
    formState.subtitle.trim() !== '' ||
    formState.selectedFormFieldIds.length > 0;

  // ─── Load form options (initial + search reset) ──────────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function loadForms() {
      setLoadingForms(true);
      const result = await listTaskForms({
        page: 1,
        limit: FORM_PAGE_LIMIT,
        ...(formSearch ? { search: formSearch } : {}),
      });
      if (cancelled) {
        return;
      }
      setLoadingForms(false);
      if (result.success && result.data) {
        setFormOptions(
          result.data.data.map((f) => ({
            value: f.id,
            label: f.name,
            formId: f.form_id,
          }))
        );
        setFormOptionsHasNextPage(result.data.pagination.hasNextPage);
        setFormPage(1);
      }
    }

    void loadForms();
    return () => {
      cancelled = true;
    };
  }, [formSearch]);

  // ─── Debounced search handler ────────────────────────────────────────────────

  const handleFormSearch = useCallback((value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setFormSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const handleFormSearchClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setFormSearch('');
  }, []);

  // ─── Infinite scroll: load next page ────────────────────────────────────────

  const handleFormScrollEnd = useCallback(async () => {
    if (!formOptionsHasNextPage || loadingMoreForms) {
      return;
    }
    const nextPage = formPage + 1;
    setLoadingMoreForms(true);
    const result = await listTaskForms({
      page: nextPage,
      limit: FORM_PAGE_LIMIT,
      ...(formSearch ? { search: formSearch } : {}),
    });
    setLoadingMoreForms(false);
    if (result.success && result.data) {
      const { data } = result;
      setFormOptions((prev) => [
        ...prev,
        ...data.data.map((f) => ({
          value: f.id,
          label: f.name,
          formId: f.form_id,
        })),
      ]);
      setFormOptionsHasNextPage(data.pagination.hasNextPage);
      setFormPage(nextPage);
    }
  }, [formOptionsHasNextPage, loadingMoreForms, formPage, formSearch]);

  // ─── Load fields when formTemplateId changes ─────────────────────────────────

  useEffect(() => {
    if (!formState.formTemplateId) {
      setFormState((prev) => ({
        ...prev,
        availableFormFields: [],
        selectedFormFieldIds: [],
      }));
      return;
    }

    let cancelled = false;

    async function loadFields() {
      setLoadingFields(true);
      const result = await getTaskForm(formState.formTemplateId);
      if (cancelled) {
        return;
      }
      setLoadingFields(false);
      if (result.success && result.data) {
        const fields = result.data.schema.sections.flatMap((s) =>
          s.fields
            .filter((f) => f.type !== 'signature')
            .map((f) => ({
              id: f.id,
              label: f.config.label,
            }))
        );
        setFormState((prev) => ({
          ...prev,
          availableFormFields: fields,
          selectedFormFieldIds: [],
        }));
        if (errors.selectedFormFieldIds) {
          setErrors((prev) => {
            const { selectedFormFieldIds: _, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    void loadFields();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.formTemplateId]);

  // ─── Field setters ───────────────────────────────────────────────────────────

  const setCertTemplateName = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, certTemplateName: value }));
      if (errors.certTemplateName) {
        setErrors((prev) => {
          const { certTemplateName: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.certTemplateName]
  );

  const setFormTemplate = useCallback(
    (id: string) => {
      const option = formOptions.find((o) => o.value === id);
      setFormState((prev) => ({
        ...prev,
        formTemplateId: id,
        formTemplateName: option?.label ?? '',
        formTemplateDisplayId: option?.formId ?? '',
      }));
      if (errors.formTemplateId) {
        setErrors((prev) => {
          const { formTemplateId: _, ...rest } = prev;
          return rest;
        });
      }
    },
    // formOptions is intentionally included so label lookup stays fresh
    [formOptions, errors.formTemplateId]
  );

  const setHeaderField = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, headerField: value }));
      if (errors.headerField) {
        setErrors((prev) => {
          const { headerField: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.headerField]
  );

  const setFooterField = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, footerField: value }));
      if (errors.footerField) {
        setErrors((prev) => {
          const { footerField: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.footerField]
  );

  const setSubtitle = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, subtitle: value }));
      if (errors.subtitle) {
        setErrors((prev) => {
          const { subtitle: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.subtitle]
  );

  const setIncludeTechnicianDetail = useCallback((value: boolean) => {
    setFormState((prev) => ({ ...prev, includeTechnicianDetail: value }));
  }, []);

  const handleToggleField = useCallback(
    (fieldId: string) => {
      setFormState((prev) => {
        const already = prev.selectedFormFieldIds.includes(fieldId);
        return {
          ...prev,
          selectedFormFieldIds: already
            ? prev.selectedFormFieldIds.filter((id) => id !== fieldId)
            : [...prev.selectedFormFieldIds, fieldId],
        };
      });
      if (errors.selectedFormFieldIds) {
        setErrors((prev) => {
          const { selectedFormFieldIds: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.selectedFormFieldIds]
  );

  // ─── Preview ─────────────────────────────────────────────────────────────────

  const handleOpenPreview = useCallback(async () => {
    // Re-fetch the form template's current fields before opening preview.
    // Guards against a 400 "field IDs do not exist" API error that occurs when
    // the form template's fields are modified externally after the user's initial
    // selection (stale selectedFormFieldIds).
    let stateToValidate = formState;

    if (formState.formTemplateId) {
      setIsPreviewValidating(true);
      try {
        const result = await getTaskForm(formState.formTemplateId);
        if (result.success && result.data) {
          const freshFields = result.data.schema.sections.flatMap((s) =>
            s.fields
              .filter((f) => f.type !== 'signature')
              .map((f) => ({ id: f.id, label: f.config.label }))
          );
          const freshFieldIds = new Set(freshFields.map((f) => f.id));
          const validSelectedIds = formState.selectedFormFieldIds.filter((id) =>
            freshFieldIds.has(id)
          );
          const hasStaleIds =
            validSelectedIds.length < formState.selectedFormFieldIds.length;

          setFormState((prev) => ({
            ...prev,
            availableFormFields: freshFields,
            selectedFormFieldIds: validSelectedIds,
          }));

          if (hasStaleIds && validSelectedIds.length === 0) {
            // All previously selected fields were removed by a form template update.
            // Use the snackbar so the user understands why (avoids a confusing
            // "select at least one field" validation error).
            setSnackbarError(
              'Some selected form fields are no longer available. Please reselect the fields.'
            );
            setIsErrorSnackbarOpen(true);
            return;
          }

          // Some (or no) stale IDs were silently pruned; valid selections remain.
          // Proceed to preview with the valid subset — no interruption needed.
          stateToValidate = {
            ...formState,
            availableFormFields: freshFields,
            selectedFormFieldIds: validSelectedIds,
          };
        }
      } finally {
        setIsPreviewValidating(false);
      }
    }

    const validationErrors = validateForPreview(stateToValidate);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      setValidationAttemptCount((n) => n + 1);
      return;
    }
    setErrors({});
    setIsPreviewOpen(true);
  }, [formState]);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  // ─── Submit (from preview) ────────────────────────────────────────────────────

  const handleSaveFromPreview = useCallback(async () => {
    setIsSubmitting(true);
    const payload = {
      cert_template_name: formState.certTemplateName.trim(),
      form_template_id: formState.formTemplateId,
      header_field: formState.headerField.trim(),
      footer_field: formState.footerField.trim(),
      include_technician_detail: formState.includeTechnicianDetail,
      selected_form_field_ids: formState.selectedFormFieldIds,
      ...(formState.subtitle.trim()
        ? { subtitle: formState.subtitle.trim() }
        : {}),
    };

    const result = await createCertificateTemplate(payload);
    setIsSubmitting(false);

    if (result.success) {
      navigate('/Certyfikaty', {
        state: { navigationSuccessMessage: CERTIFICATE_LABELS.CREATE_SUCCESS },
      });
    } else {
      setSnackbarError(result.error ?? CERTIFICATE_LABELS.CREATE_SUCCESS);
      setIsErrorSnackbarOpen(true);
    }
  }, [formState, navigate]);

  // ─── Cancel ───────────────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsCancelModalOpen(true);
    } else {
      navigate('/Certyfikaty');
    }
  }, [isDirty, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Certyfikaty');
  }, [navigate]);

  const handleCloseErrorSnackbar = useCallback(() => {
    setIsErrorSnackbarOpen(false);
  }, []);

  return {
    formState,
    formOptions,
    formOptionsHasNextPage,
    loadingForms,
    loadingMoreForms,
    loadingFields,
    isPreviewValidating,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setCertTemplateName,
    setFormTemplate,
    handleFormSearch,
    handleFormSearchClear,
    handleFormScrollEnd,
    setHeaderField,
    setFooterField,
    setSubtitle,
    setIncludeTechnicianDetail,
    handleToggleField,
    handleOpenPreview,
    handleClosePreview,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
  };
}
